/**
 * The side drawer for one step — the concept's `flow/Drawer.jsx` frame:
 * icon, name, "Changes save automatically", the form, and a footer with the
 * step's connections, Delete step and Done.
 *
 * Two things the reference did not need and a real editor does:
 *
 *   - **Connections are listed and can be cut.** The reference only ever
 *     removed an edge by removing a step; here a path can be disconnected
 *     from its source step without deleting what it led to.
 *   - **The step's own problem is shown at the top.** When autosave holds a
 *     draft back because the contract refuses something on this step (an
 *     empty "If empty", a tag with no text), the sentence sits here, where the
 *     field is — not only in the top bar.
 *
 * In run mode the drawer is read-only: the live numbers, the people at this
 * step, and each person's step log. Escape closes it and focus returns to the
 * step it was opened from.
 */
import { edgeFrom, portsOf, type FlowEdge, type FlowGraph, type FlowNode, type FlowNodeStats, type FlowSettings } from '@wend/shared';
import { cn } from '@wend/ui';
import { useEffect, useRef, type KeyboardEvent } from 'react';

import { HINT } from './controls';
import {
  BTN_GHOST,
  CommentForm,
  ConditionForm,
  ExitForm,
  GoalForm,
  ICON_BTN,
  MessageForm,
  SplitForm,
  TriggerForm,
  UpdateForm,
  WaitForm,
  portLabel,
} from './forms';
import { IcClose, IcTrash } from './icons';
import { NodeIcon, nodeTone, type FlowMode } from './Node';
import { nodeName, type FlowEditorData } from './model';
import { RunBreakdown, RunPeople, RunTiles, type RunPeopleApi } from './RunView';

export interface DrawerProps {
  node: FlowNode;
  mode: FlowMode;
  graph: FlowGraph;
  settings: FlowSettings;
  data: FlowEditorData;
  stat: FlowNodeStats | null;
  flowId: string;
  runVersion: number | null;
  peopleApi: RunPeopleApi;
  problem: string | null;
  canEnrol: boolean;
  enrolling: boolean;
  onEnrol: (segmentId: string) => void;
  onReplace: (node: FlowNode) => void;
  onSettings: (settings: FlowSettings) => void;
  onDelete: (id: string) => void;
  onDisconnect: (edgeId: string) => void;
  onClose: () => void;
  newId: (prefix: string) => string;
  /** Draw as a full-width sheet (the list view below 640px) rather than a side panel. */
  sheet?: boolean;
}

export function Drawer({
  node,
  mode,
  graph,
  settings,
  data,
  stat,
  flowId,
  runVersion,
  peopleApi,
  problem,
  canEnrol,
  enrolling,
  onEnrol,
  onReplace,
  onSettings,
  onDelete,
  onDisconnect,
  onClose,
  newId,
  sheet = false,
}: DrawerProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[data-dclose]')?.focus();
  }, [node.id]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const name = nodeName(node);
  const editable = mode === 'edit' && node.suggested === undefined;
  const incoming = graph.edges.filter((e) => e.to === node.id).length;
  const outgoing: FlowEdge[] = portsOf(node).flatMap((p) => {
    const edge = edgeFrom(graph, node.id, p.id);
    return edge ? [edge] : [];
  });

  let form = null;
  if (mode === 'run') {
    form = (
      <>
        <RunTiles node={node} stat={stat} />
        {stat && <RunBreakdown node={node} stat={stat} data={data} />}
        {runVersion !== null && node.type !== 'comment' && <RunPeople flowId={flowId} nodeId={node.id} version={runVersion} api={peopleApi} />}
      </>
    );
  } else if (!editable) {
    form = <p className={HINT}>This is a suggestion. Accept it on the canvas (✓) to edit it like any other step, or reject it (✕).</p>;
  } else {
    switch (node.type) {
      case 'trigger':
        form = <TriggerForm settings={settings} data={data} onChange={onSettings} canEnrol={canEnrol} onEnrol={onEnrol} enrolling={enrolling} />;
        break;
      case 'message':
        form = <MessageForm node={node} data={data} onReplace={onReplace} edgesFrom={outgoing.length} />;
        break;
      case 'wait':
        form = <WaitForm node={node} onReplace={onReplace} />;
        break;
      case 'split':
        form = <SplitForm node={node} onReplace={onReplace} newRowId={() => newId('p')} />;
        break;
      case 'condition':
        form = <ConditionForm node={node} data={data} onReplace={onReplace} newId={() => newId('r')} />;
        break;
      case 'update':
        form = <UpdateForm node={node} data={data} onReplace={onReplace} />;
        break;
      case 'goal':
        form = <GoalForm node={node} data={data} onReplace={onReplace} />;
        break;
      case 'exit':
        form = <ExitForm node={node} onReplace={onReplace} />;
        break;
      case 'comment':
        form = <CommentForm node={node} onReplace={onReplace} />;
        break;
    }
  }

  const byId = new Map(graph.nodes.map((x) => [x.id, x]));

  return (
    <aside
      ref={ref}
      aria-label={`${name} settings`}
      onKeyDown={onKeyDown}
      data-flow-drawer=""
      className={cn(
        'z-[25] flex flex-col border-line-strong bg-[rgba(25,19,56,.97)] shadow-[-24px_0_50px_-20px_rgba(0,0,0,.75)] backdrop-blur-[10px]',
        sheet ? 'relative mt-3 rounded-xl border' : 'absolute inset-y-0 right-0 w-[min(372px,100%)] border-l motion-safe:animate-pop',
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
        <span className={cn('grid size-8 flex-none place-items-center rounded-[9px] bg-white/[0.07]', nodeTone(node), (node.type === 'split' || node.type === 'wait' || node.type === 'condition') && 'bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] text-white')}>
          <NodeIcon node={node} size={16} />
        </span>
        <div>
          <div className="text-[15px] font-semibold leading-tight text-white">{name}</div>
          <div className="text-[12px] text-muted">
            {mode === 'run' ? 'Live counts · read-only while running' : node.suggested ? 'Suggested — accept it on the canvas to keep it' : 'Changes save automatically'}
          </div>
        </div>
        <button type="button" data-dclose="" className={cn(ICON_BTN, 'ml-auto')} aria-label="Close settings" onClick={onClose}>
          <IcClose size={16} />
        </button>
      </div>
      <div className="flex flex-1 flex-col gap-3.5 overflow-auto p-4">
        {problem && mode === 'edit' && (
          <p role="alert" className="m-0 rounded-[10px] border border-warn/40 bg-warn/8 px-3 py-2 text-[12.5px] text-warn-ink">
            Not saved yet — {problem}
          </p>
        )}
        {form}
        {mode === 'edit' && outgoing.length > 0 && (
          <section aria-label="Connections" className="flex flex-col gap-1.5">
            <p className="m-0 font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted">Leads to</p>
            {outgoing.map((edge) => {
              const to = byId.get(edge.to);
              return (
                <div key={edge.id} className="flex items-center justify-between gap-2 rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]">
                  <span className="min-w-0 truncate">
                    <span className="text-muted">{portLabel(node, edge.port, data)}</span> → {to ? nodeName(to) : 'a missing step'}
                  </span>
                  <button type="button" className="flex-none cursor-pointer rounded-md px-1.5 py-0.5 text-[12px] text-brand-ink underline underline-offset-2 hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b" onClick={() => onDisconnect(edge.id)}>
                    Disconnect
                  </button>
                </div>
              );
            })}
          </section>
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-line px-4 py-3">
        <span className={cn(HINT, 'mr-auto')}>
          {incoming} in · {outgoing.length} out
        </span>
        {mode === 'edit' && node.type !== 'trigger' && (
          <button type="button" className={cn(BTN_GHOST, 'border-bad/35 bg-transparent text-bad')} onClick={() => onDelete(node.id)}>
            <IcTrash size={14} /> Delete step
          </button>
        )}
        <button type="button" onClick={onClose} className="inline-flex cursor-pointer items-center rounded-[9px] bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] px-3.5 py-2 text-[13px] font-medium text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b">
          Done
        </button>
      </div>
    </aside>
  );
}
